import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_DRAWER_WIDTH,
  IssuesFilterByOption,
  IssuesSortByOption,
  MetricsFilterByOption,
  SortByDirection,
  TaskItemsFilterByOption,
  TaskItemsSortByOption,
  TeamIssuesGroupByOption,
  TeamTaskItemsGroupByOption,
  UserIssuesGroupByOption,
  UserStrategyFilterByOption,
  UserStrategyGroupByOption,
  UserStrategySortByOption,
  UserTaskItemsGroupByOption,
} from "../constants";
import {
  FeatureFlag,
  ICoherenceReportSettings,
  IDataGridSettings,
  IDrawerSettings,
  IHomeSettings,
  IMeetingSettings,
  ISearchSettings,
  ISettingsData,
  ISideMenuSettings,
  ITableSettings,
  ITeamImplementationSettings,
  ITeamMetricsSettings,
  ITeamSettings,
  ITeamStrategySettings,
  IUserImplementationSettings,
  IUserStrategySettings,
  useLocalStorage,
} from "../hooks";

interface ISettingsContext extends ISettingsData {
  updateHomeSettings: (settings: IHomeSettings) => void;
  updateSideMenuSettings: (settings: ISideMenuSettings) => void;
  updateDrawerSettings: (settings: IDrawerSettings) => void;
  updateTeamSettings: (settings: ITeamSettings) => void;
  updateTeamStrategySettings: (planGuid: string, settings: Partial<ITeamStrategySettings>) => void;
  updateTeamImplementationSettings: (teamSlug: string, settings: Partial<ITeamImplementationSettings>) => void;
  updateTeamMetricsSettings: (teamSlug: string, settings: Partial<ITeamMetricsSettings>) => void;
  updateUserStrategySettings: (userGuid: string, settings: Partial<IUserStrategySettings>) => void;
  updateUserImplementationSettings: (userGuid: string, settings: Partial<IUserImplementationSettings>) => void;
  updateSearchSettings: (settings: ISearchSettings) => void;
  updateFeatureFlag: (featureFlag: FeatureFlag, value: boolean) => void;
  updateDataGridSettings: (dataGridId: string, settings: Partial<IDataGridSettings>) => void;
  updateTableSettings: (tableId: string, settings: Partial<ITableSettings>) => void;
  updateCoherenceReportSettings: (settings: Partial<ICoherenceReportSettings>) => void;
  updateMeetingSettings: (meetingGuid: string, settings: Partial<IMeetingSettings>) => void;
}

const defaultSettingsData: ISettingsData = {
  homeSettings: {
    showPeople: false,
    selectedTeamGuids: [],
  },
  sideMenuSettings: {
    isExpanded: false,
    expandedWidth: 240,
  },
  drawerSettings: {
    width: DEFAULT_DRAWER_WIDTH,
  },
  teamSettings: {
    networkListView: false,
    expandedSection: undefined,
  },
  teamStrategySettings: {},
  teamImplementationSettings: {},
  teamMetricsSettings: {},
  userStrategySettings: {},
  userImplementationSettings: {},
  searchSettings: {
    searchHistory: [],
  },
  featureFlags: {
    surveys: false,
    meetings: false,
    chatbot: false,
    snowfall: false,
  },
  dataGridSettings: {},
  tableSettings: {},
  coherenceReportSettings: {
    selectedTeamGuids: [],
    comparisonTeamGuids: undefined,
  },
  meetingSettings: {},
};

const defaultUserStrategySettings: IUserStrategySettings = {
  tableView: true,
  groupByOption: UserStrategyGroupByOption.Team,
  filterByOption: UserStrategyFilterByOption.All,
  filterByStatusOption: undefined,
  sortByOption: UserStrategySortByOption.DateModified,
  sortByDirection: SortByDirection.Descending,
};

const defaultUserImplementationSettings: IUserImplementationSettings = {
  tableView: true,
  taskItemsGroupByOption: UserTaskItemsGroupByOption.InFocus,
  taskItemsFilterByOption: TaskItemsFilterByOption.All,
  taskItemsFilterByStatusOption: undefined,
  taskItemsSortByOption: TaskItemsSortByOption.DateCreated,
  taskItemsSortByDirection: SortByDirection.Descending,
  issuesGroupByOption: UserIssuesGroupByOption.None,
  issuesFilterByOption: IssuesFilterByOption.All,
  issuesFilterByStatusOption: undefined,
  issuesSortByOption: IssuesSortByOption.Upvotes,
  issuesSortByDirection: SortByDirection.Descending,
};

const defaultTeamStrategySettings: ITeamStrategySettings = {
  tableView: true,
};

const defaultTeamImplementationSettings: ITeamImplementationSettings = {
  tableView: true,
  taskItemsGroupByOption: TeamTaskItemsGroupByOption.None,
  taskItemsFilterByOption: TaskItemsFilterByOption.All,
  taskItemsFilterByStatusOption: undefined,
  taskItemsSortByOption: TaskItemsSortByOption.DateCreated,
  taskItemsSortByDirection: SortByDirection.Descending,
  issuesGroupByOption: TeamIssuesGroupByOption.None,
  issuesFilterByOption: IssuesFilterByOption.All,
  issuesFilterByStatusOption: undefined,
  issuesSortByOption: IssuesSortByOption.Upvotes,
  issuesSortByDirection: SortByDirection.Descending,
};

const defaultTeamMetricsSettings: ITeamMetricsSettings = {
  filterByOption: MetricsFilterByOption.All,
  filterByValue: undefined,
};

const defaultDataGridSettings: IDataGridSettings = {
  columnVisibility: {},
};

const defaultTableSettings: ITableSettings = {
  columnVisibility: {},
};

const defaultMeetingSettings: IMeetingSettings = {
  filterByOption: MetricsFilterByOption.All,
  filterByValue: undefined,
};

const SettingsContext = React.createContext<ISettingsContext>({
  ...defaultSettingsData,
  updateHomeSettings: () => null,
  updateSideMenuSettings: () => null,
  updateDrawerSettings: () => null,
  updateTeamSettings: () => null,
  updateTeamStrategySettings: () => null,
  updateTeamImplementationSettings: () => null,
  updateTeamMetricsSettings: () => null,
  updateUserStrategySettings: () => null,
  updateUserImplementationSettings: () => null,
  updateSearchSettings: () => null,
  updateFeatureFlag: () => null,
  updateDataGridSettings: () => null,
  updateTableSettings: () => null,
  updateCoherenceReportSettings: () => null,
  updateMeetingSettings: () => null,
});

interface IProps {
  children?: React.ReactNode;
}

const SettingsProvider = (props: IProps) => {
  const { getLocalStorageSettings, setLocalStorageSettings } = useLocalStorage();
  const [settings, setSettings] = useState<ISettingsData>(defaultSettingsData);

  useEffect(() => {
    const localStorageSettings = getLocalStorageSettings();

    if (localStorageSettings != null) {
      setSettings(_.merge(settings, localStorageSettings));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLocalStorageSettings(settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings]);

  function updateTeamSettings(teamSettings: ITeamSettings) {
    setSettings({
      ...settings,
      teamSettings: teamSettings,
    });
  }

  function updateHomeSettings(homeSettings: IHomeSettings) {
    setSettings({
      ...settings,
      homeSettings: homeSettings,
    });
  }

  function updateSideMenuSettings(sideMenuSettings: ISideMenuSettings) {
    setSettings({
      ...settings,
      sideMenuSettings: sideMenuSettings,
    });
  }

  function updateDrawerSettings(drawerSettings: IDrawerSettings) {
    setSettings({
      ...settings,
      drawerSettings: drawerSettings,
    });
  }

  function updateTeamStrategySettings(teamSlug: string, teamStrategySettings: Partial<ITeamStrategySettings>) {
    setSettings({
      ...settings,
      teamStrategySettings: {
        ...settings.teamStrategySettings,
        [teamSlug]: {
          ...(settings.teamStrategySettings[teamSlug] ?? { ...defaultTeamStrategySettings }),
          ...teamStrategySettings,
        },
      },
    });
  }

  function updateTeamImplementationSettings(
    teamSlug: string,
    teamImplementationSettings: Partial<ITeamImplementationSettings>
  ) {
    setSettings({
      ...settings,
      teamImplementationSettings: {
        ...settings.teamImplementationSettings,
        [teamSlug]: {
          ...(settings.teamImplementationSettings[teamSlug] ?? { ...defaultTeamImplementationSettings }),
          ...teamImplementationSettings,
        },
      },
    });
  }

  function updateTeamMetricsSettings(teamSlug: string, teamMetricsSettings: Partial<ITeamMetricsSettings>) {
    setSettings({
      ...settings,
      teamMetricsSettings: {
        ...settings.teamMetricsSettings,
        [teamSlug]: {
          ...(settings.teamMetricsSettings[teamSlug] ?? { ...defaultTeamMetricsSettings }),
          ...teamMetricsSettings,
        },
      },
    });
  }

  function updateUserStrategySettings(userGuid: string, userStrategySettings: Partial<IUserStrategySettings>) {
    setSettings({
      ...settings,
      userStrategySettings: {
        ...settings.userStrategySettings,
        [userGuid]: {
          ...(settings.userStrategySettings[userGuid] ?? { ...defaultUserStrategySettings }),
          ...userStrategySettings,
        },
      },
    });
  }

  function updateUserImplementationSettings(
    userGuid: string,
    userImplementationSettings: Partial<IUserImplementationSettings>
  ) {
    setSettings({
      ...settings,
      userImplementationSettings: {
        ...settings.userImplementationSettings,
        [userGuid]: {
          ...(settings.userImplementationSettings[userGuid] ?? { ...defaultUserImplementationSettings }),
          ...userImplementationSettings,
        },
      },
    });
  }

  function updateSearchSettings(searchSettings: ISearchSettings) {
    setSettings({
      ...settings,
      searchSettings: searchSettings,
    });
  }

  function updateDataGridSettings(dataGridId: string, dataGridSettings: Partial<IDataGridSettings>) {
    setSettings({
      ...settings,
      dataGridSettings: {
        ...settings.dataGridSettings,
        [dataGridId]: {
          ...(settings.dataGridSettings[dataGridId] ?? { ...defaultDataGridSettings }),
          ...dataGridSettings,
        },
      },
    });
  }

  function updateTableSettings(tableId: string, tableSettings: Partial<ITableSettings>) {
    setSettings({
      ...settings,
      tableSettings: {
        ...settings.tableSettings,
        [tableId]: {
          ...(settings.tableSettings[tableId] ?? { ...defaultTableSettings }),
          ...tableSettings,
        },
      },
    });
  }

  function updateCoherenceReportSettings(coherenceReportSettings: Partial<ICoherenceReportSettings>) {
    setSettings({
      ...settings,
      coherenceReportSettings: {
        ...settings.coherenceReportSettings,
        ...coherenceReportSettings,
      },
    });
  }

  function updateMeetingSettings(meetingGuid: string, meetingSettings: Partial<IMeetingSettings>) {
    setSettings({
      ...settings,
      meetingSettings: {
        ...settings.meetingSettings,
        [meetingGuid]: {
          ...(settings.meetingSettings[meetingGuid] ?? { ...defaultMeetingSettings }),
          ...meetingSettings,
        },
      },
    });
  }

  const updateFeatureFlag = useCallback(
    (featureFlag: FeatureFlag, value: boolean) => {
      setSettings({
        ...settings,
        featureFlags: {
          ...settings.featureFlags,
          [featureFlag]: value,
        },
      });

      return `Feature flag "${featureFlag}" set to "${value}"`;
    },
    [settings]
  );

  useEffect(() => {
    // @ts-ignore
    window.toggleMeetings = () => updateFeatureFlag("meetings", !settings.featureFlags.meetings);

    // @ts-ignore
    window.toggleSurveys = () => updateFeatureFlag("surveys", !settings.featureFlags.surveys);

    // @ts-ignore
    window.toggleChatbot = () => updateFeatureFlag("chatbot", !settings.featureFlags.chatbot);
  }, [settings.featureFlags.meetings, settings.featureFlags.surveys, settings.featureFlags.chatbot, updateFeatureFlag]);

  return (
    <SettingsContext.Provider
      value={{
        ...settings,
        updateHomeSettings,
        updateSideMenuSettings,
        updateDrawerSettings,
        updateTeamSettings,
        updateTeamStrategySettings,
        updateTeamImplementationSettings,
        updateTeamMetricsSettings,
        updateUserStrategySettings,
        updateUserImplementationSettings,
        updateSearchSettings,
        updateFeatureFlag,
        updateDataGridSettings,
        updateTableSettings,
        updateCoherenceReportSettings,
        updateMeetingSettings,
      }}
    >
      {props.children}
    </SettingsContext.Provider>
  );
};

export {
  defaultDataGridSettings,
  defaultTableSettings,
  defaultTeamImplementationSettings,
  defaultTeamMetricsSettings,
  defaultTeamStrategySettings,
  defaultUserImplementationSettings,
  defaultUserStrategySettings,
  defaultMeetingSettings,
  SettingsContext,
  SettingsProvider,
};
